<template>
    <!-- We need to show this button for a selected filter, so that it's not changeable -->
    <nord-button size="s" :variant="variant" v-if="hideDropdown">
        {{ selectedItem ? selectedItem[labelKey] : placeholderText }}
    </nord-button>

    <!-- Else we show this dropdown for an unselected filter -->
    <nord-dropdown size="s" ref="dropdown" v-else>
        <nord-button
            size="s"
            slot="toggle"
            :variant="variant"
        >
            <nord-icon
                v-if="showNordStartIcon"
                slot="start"
                :name="icon"
            />
            {{ multiSelect ? buttonTextMultiSelect : buttonText }}
            <nord-icon size="s" v-show="false"></nord-icon>
        </nord-button>

        <nord-dropdown-item
            v-for="item in items"
            :key="item[valueKey]"
            @click="multiSelect ? selectItems(item) : selectItem(item)"
        >
            {{ item[labelKey] }}
            <nord-icon
                slot="end"
                name="interface-checked"
                v-show="isSelected(item)"
            />
        </nord-dropdown-item>

        <!-- Apply button for multi-select mode -->
        <nord-dropdown-item
            v-if="multiSelect && localSelectedItems.length > 0"
            @click="applyMultiSelect"
        >
            <nord-button size="s" variant="primary" expand>
                {{ trans('tables.filters.actions.apply', { count: localSelectedItems.length }) }}
            </nord-button>
        </nord-dropdown-item>
    </nord-dropdown>
</template>

<script setup>
import { ref, computed, defineEmits, defineProps, watch } from 'vue';

const props = defineProps({
    type: {
        type: String,
        required: true,
        validator: (value) => {
            return ['field', 'operator', 'value'].includes(value);
        }
    },

    items: {
        type: Array,
        default: () => ([])
    },

    labelKey: {
        type: String,
        default: 'label'
    },

    valueKey: {
        type: String,
        default: 'value'
    },

    selectedItem: {
        type: [Object, null],
        default: null
    },

    selectedItems: {
        type: Array,
        default: () => ([])
    },

    placeholderText: {
        type: String,
        default: () => trans('tables.filters.placeholders.selectItem')
    },

    variant: {
        type: String,
        default: 'default'
    },

    hideDropdown: {
        type: Boolean,
        default: false
    },

    icon: {
        type: String,
        default: ''
    },

    multiSelect: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['item-selected', 'items-selected']);

const dropdown = ref(null);

const buttonText = computed(() => {
    return props.selectedItem
        ? props.selectedItem[props.labelKey]
        : props.placeholderText;
});

function selectItem(item) {
    emit('item-selected', item);

    if (props.type !== 'value' && dropdown.value?.hide) {
        dropdown.value.hide();
    }
}

const showNordStartIcon = computed(() => {
    if (!props.icon) {
        return false;
    }

    if (props.multiSelect && localSelectedItems.value.length === 0) {
        return true;
    }

    return false;
});

/**
 * Multi-select mode
 */
const localSelectedItems = ref([]);

function selectItems(item) {
    const index = localSelectedItems.value.findIndex(
        selectedItem => selectedItem[props.valueKey] === item[props.valueKey]
    );

    if (index !== -1) {
        localSelectedItems.value.splice(index, 1);
    } else {
        localSelectedItems.value.push(item);
    }
}

function isSelected(item) {
    // Multi-select mode
    if (props.multiSelect) {
        return localSelectedItems.value.some(
            selectedItem => selectedItem[props.valueKey] === item[props.valueKey]
        );
    }

    // Single-select mode
    if (!props.selectedItem || !item) return false;

    return item[props.valueKey] === props.selectedItem[props.valueKey];
}

function applyMultiSelect() {
    emit('items-selected', [...localSelectedItems.value]);

    if (dropdown.value?.hide) {
        dropdown.value.hide();
    }
}

const buttonTextMultiSelect = computed(() => {
    // comma separated list of selected items
    if (localSelectedItems.value.length > 1) {
        return localSelectedItems.value.map(item => item[props.labelKey]).join(', ');
    }

    // single selected item
    if (localSelectedItems.value.length === 1) {
        return localSelectedItems.value[0][props.labelKey];
    }

    return props.placeholderText;
});

// Watch for changes in selectedItems prop (multi-select)
watch(() => props.selectedItems, (newItems) => {
    if (props.multiSelect) {
        localSelectedItems.value = [...newItems];
    }
}, { immediate: true });

</script>
